// filepath: apps/web/src/i18n/localeMap.ts
//
// FR-UI-CORE-9 — LocaleMap resolver helper.
// FR-I18N-3 — extended locale set to include `uk` and `be`.
//
// Wire-shape extension (`UiSchema.fields[id].label/help`) uses
// the `LocaleMap` shape `{ ru?: string; kk?: string; en?: string; uk?: string; be?: string }`
// defined in `@/i18n/useTranslation.ts`. This module adds the
// `resolveLocalizedValue` helper that walks the same fallback
// chain as `resolveLocaleLabel` but accepts a `string | LocaleMap`
// input — i.e. a definition may carry either a single string
// (locale-agnostic) OR a per-locale map.
//
// SECURITY (INV-2 boundary): the resolved value is consumed only
// as a React text node / ARIA attribute. No string concatenation
// or HTML interpretation. Static guard at
// `apps/web/src/runtime/staticGuards.test.ts` INV-9 assertion
// auto-scans this file.

import type { Locale, LocaleMap } from "./useTranslation";
import { FALLBACK_ORDER } from "./locales";

/**
 * Resolve a locale-aware value with a guaranteed non-empty result.
 *
 * Order of resolution:
 *   1. If `value` is `undefined` / `null`, return `fallback`.
 *   2. If `value` is a non-empty `string`, return it.
 *   3. If `value` is a `LocaleMap`, walk `locale` → `FALLBACK_ORDER`
 *      and return the first non-empty entry. Empty string is
 *      treated as absent (matches `resolveLocaleLabel` semantics).
 *   4. If no entry is found, return `fallback`.
 *
 * @param value    The locale-aware value (`string | LocaleMap | undefined`).
 * @param locale   Currently-active locale.
 * @param fallback Final fallback used when every other path yields
 *                 no value. Must be a non-empty string — the function
 *                 guarantees a non-empty return for the caller's
 *                 downstream `aria-label` / `<label>` usage.
 */
export function resolveLocalizedValue(
  value: string | LocaleMap | undefined | null,
  locale: Locale,
  fallback: string,
): string {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") {
    return value.length > 0 ? value : fallback;
  }
  // LocaleMap path — walk active locale first, then fallback chain.
  const direct = value[locale];
  if (typeof direct === "string" && direct.length > 0) return direct;
  for (const fb of FALLBACK_ORDER) {
    const entry = value[fb];
    if (typeof entry === "string" && entry.length > 0) return entry;
  }
  return fallback;
}
